import { Component, Input, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, finalize } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { EventAttendance, EventAttendanceStatus } from '../../models/event';
import { AttendanceService, AttendanceStatusMetadata } from '../../services/attendance.service';
import { UserService } from '../../services/user.service';
import { AttendanceUser } from '../../sessions/session/attendance/attendance-ui-models';
import { ModalManagerService } from '../../services/modal-manager.service';
import { AttendanceNotesComponent } from '../attendance-notes/attendance-notes.component';
import { StatusColor } from '../../standalones/components/status-indicator/status-indicator.component';

export enum AttendanceStatusMode {
  EDITABLE = 'editable', // Can edit status and view/edit notes
  VIEW_ONLY = 'view_only', // Can't edit status but can view notes
  READ_ONLY = 'read_only', // Can't edit status or click the button
}

@UntilDestroy()
@Component({
  selector: 'app-attendance-status-indicator',
  templateUrl: './attendance-status-indicator.component.html',
  styleUrls: ['./attendance-status-indicator.component.scss'],
})
export class AttendanceStatusIndicatorComponent implements OnInit {
  /**
   * The current attendance status of the user for the event
   * that determines which status is displayed as selected
   * we don't use status and notes here from the attendance user,
   * we're using it from the eventAttendance input
   */
  @Input() attendanceUser!: AttendanceUser;

  /**
   * The ID of the event for which we're displaying/updating attendance status
   */
  @Input() eventId!: string;

  /**
   * List of available attendance statuses that can be selected
   * Defaults to all updatable statuses from the AttendanceService except canceled
   */
  @Input() attendanceStatuses: AttendanceStatusMetadata[] =
    this.attendanceService.updateableAttendanceStatusesMetadata;

  /**
   * The mode of the attendance status indicator that determines
   * what actions are available to the user
   * Defaults to EDITABLE mode
   */
  @Input() mode: AttendanceStatusMode = AttendanceStatusMode.VIEW_ONLY;

  isUpdating$ = new BehaviorSubject<boolean>(false);
  // Define the status color mapping within the component
  attendanceStatusColorMap: Record<EventAttendanceStatus, StatusColor> = {
    [EventAttendanceStatus.EXCUSED]: StatusColor.PRIMARY,
    [EventAttendanceStatus.PRESENT]: StatusColor.SUCCESS,
    [EventAttendanceStatus.LATE]: StatusColor.WARNING,
    [EventAttendanceStatus.ABSENT]: StatusColor.ERROR,
    [EventAttendanceStatus.LEFT]: StatusColor.INFO,
    [EventAttendanceStatus.CANCELLED]: StatusColor.ERROR,
  };

  EventAttendanceStatus = EventAttendanceStatus;

  statusButtonTooltipText = '';
  shouldDisableButton = false;
  isCancelled = false;
  readonly AttendanceStatusMode = AttendanceStatusMode;

  constructor(
    public attendanceService: AttendanceService,
    private userService: UserService,
    private modalManagerService: ModalManagerService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    const hasNotes = !!this.attendanceUser?.notes;

    this.isCancelled = this.attendanceUser?.status === EventAttendanceStatus.CANCELLED;

    // change from edit mode to view only mode if event is cancelled
    this.mode =
      this.isCancelled && this.mode === AttendanceStatusMode.EDITABLE
        ? AttendanceStatusMode.VIEW_ONLY
        : this.mode;

    // check if button ripple should be disabled
    this.shouldDisableButton =
      this.mode === AttendanceStatusMode.READ_ONLY ||
      (this.mode === AttendanceStatusMode.VIEW_ONLY && !hasNotes);

    // Simplified tooltip logic
    const canViewNotes = this.mode === AttendanceStatusMode.VIEW_ONLY && hasNotes;

    if (canViewNotes) {
      this.statusButtonTooltipText = this.translate.instant('View Note');
      // When an event is canceled, notes are mandatory in the cancellation flow
      // Users cannot cancel events without providing a reason/note
      // So we can be 100% certain that canceled events will always have an associated note
      if (this.isCancelled) {
        this.statusButtonTooltipText = this.translate.instant(
          'Attendance for cancelled events cannot be changed. Click to view attendance note.',
        );
      }
    }
  }

  onStatusSelect(newStatus: EventAttendanceStatus): void {
    if (this.attendanceUser?.status === newStatus) {
      return;
    }

    this.updateEventAttendance({ status: newStatus });
  }

  /**
   * Updates a user's attendance status and/or notes for a specific event.
   *
   * This method implements an optimistic update pattern:
   * Stores the current state for potential rollback
   * Sends the update to the server
   * On success: Maintains the updated state and notes
   *
   * @returns {void}
   */
  private updateEventAttendance(eventAttendance: Partial<EventAttendance>): void {
    // show the loading spinner
    this.isUpdating$.next(true);
    this.attendanceService
      .updateUserAttendanceInEvent(this.eventId, {
        user_id: this.attendanceUser._id,
        modifier_host_id: this.userService.userId,
        ...eventAttendance, // Use the input directly since it's already correctly structured
      })
      .pipe(
        untilDestroyed(this),
        finalize(() => {
          this.isUpdating$.next(false);
        }),
      )
      .subscribe(() => {
        this.updateLocalState(eventAttendance);
      });
  }

  /**
   * Updates the local component state with new attendance data
   * @param attendanceUser Partial attendance data containing status and/or notes
   */
  private updateLocalState(attendanceUser: Partial<EventAttendance>): void {
    this.attendanceUser = { ...this.attendanceUser, ...attendanceUser };
  }

  trackByStatusCode(index: number, item: AttendanceStatusMetadata): string {
    return item.statusCode;
  }

  /**
   * Opens a modal dialog for editing attendance notes.
   * Handles the update of notes when the dialog is closed with valid input.
   *
   * @returns void
   */
  showNotesDialog(isViewMode = false) {
    const dialogConfig = {
      panelClass: 'attendance-note-dialog',
      disableClose: true,
      restoreFocus: false,
      data: {
        user: {
          ...this.attendanceUser,
          notes: this.attendanceUser.notes,
          status: this.attendanceUser.status,
        },
        eventId: this.eventId,
        isViewMode,
      },
    };
    this.modalManagerService.showModal(AttendanceNotesComponent, dialogConfig, {
      afterClosed: (notes: string | undefined) => {
        if (notes === undefined) {
          return;
        }

        this.updateEventAttendance({ notes: notes });
      },
    });
  }

  /**
   * Handles click events on the status pill button in non-editable mode
   * Opens the notes dialog in view-only mode when:
   * 1. Mode is VIEW_ONLY
   * 2. There are existing notes for the attendance
   */
  handleStatusPillClick() {
    if (this.statusButtonTooltipText) {
      this.showNotesDialog(true);
    }
  }
}
